import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { REFERRER_TYPE, PAGE_TYPE } from '../../../constants/gaEnums';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Divider, Grid, Stack, useMediaQuery } from '@mui/material';
import theme from '../../../theme';
import SubTab from '../../../components/button/SubTab';
import Pagination from '../../../components/common/Pagination';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import InFeedAd from '../../../components/ad/InFeedAd';
import BookmarkButton from '../../../components/button/BookmarkButton';

// Cards — horizontal (xs)
import AlbumHorizontalCard from '../../../components/card/AlbumHorizontalCard';
import NewsCard from '../../../components/card/NewsCard';
import NoticeCard from '../../../components/card/NoticeCard';

// Cards — vertical (md+)
import AlbumVerticalCard from '../../../components/card/AlbumVerticalCard';
import NewsVerticalCard from '../../../components/card/NewsVerticalCard';
import PlaylistVerticalCard from '../../../components/card/PlaylistVerticalCard';
import PlaylistCard from '../../../components/card/PlaylistCard';
import NoticeVerticalCard from '../../../components/card/NoticeVerticalCard';
import ReviewCard from '../../../components/card/ReviewCard';

import { useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '../../../api/reviews';
import { newsApi } from '../../../api/news';
import { playlistApi } from '../../../api/playlist';
import { noticesApi } from '../../../api/notices';
import { feedsApi } from '../../../api/feeds';
import FeedCard from '../../feed/FeedCard';

const SUB_TABS = [
  { label: '앨범', key: 'albums', categories: ['Albums'] },
  { label: '리뷰', key: 'reviews', categories: ['Reviews'] },
  { label: '플리', key: 'lists', categories: ['Lists'] },
  { label: '공지', key: 'notice', categories: ['Notice'] },
  { label: '피드', key: 'feed', categories: ['Feed'] },
];

const ITEMS_PER_PAGE_XS = 10;
const ITEMS_PER_PAGE_MD = 40;
const ITEMS_PER_AD_XS = 5;
const ITEMS_PER_AD_MD = 20;

const DEFAULT_TYPE = 'albums';
const TYPE_KEYS = SUB_TABS.map((t) => t.key);

const SavedTab = ({ api, userId, readOnly }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookmarkError, setBookmarkError] = useState('');
  const pendingBookmarks = useRef(new Set());
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // URL에서 상태 읽기
  const urlType = searchParams.get('type');
  const typeTab = TYPE_KEYS.includes(urlType) ? urlType : DEFAULT_TYPE;
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const itemsPerPage = isXs ? ITEMS_PER_PAGE_XS : ITEMS_PER_PAGE_MD;
  const itemsPerAd = isXs ? ITEMS_PER_AD_XS : ITEMS_PER_AD_MD;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await (userId ? api(userId) : api());
      setAllData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch bookmark data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 카운트 계산
  const countByType = useMemo(() => {
    const counts = {};
    SUB_TABS.forEach((tab) => {
      counts[tab.key] = allData.filter(
        (item) => tab.categories.includes(item.category) && getItemKey(item) != null,
      ).length;
    });
    return counts;
  }, [allData]);

  const subTabsWithCount = useMemo(
    () =>
      SUB_TABS.map((tab) => ({
        ...tab,
        label: `${tab.label} (${countByType[tab.key] ?? 0})`,
      })),
    [countByType],
  );

  // 카테고리별 필터링 (유효한 아이템만)
  const filteredData = useMemo(() => {
    const categories = SUB_TABS.find((t) => t.key === typeTab)?.categories || [];
    return allData.filter((item) => categories.includes(item.category) && getItemKey(item) != null);
  }, [allData, typeTab]);

  // 페이지네이션
  const pageCount = useMemo(
    () => Math.ceil(filteredData.length / itemsPerPage),
    [filteredData, itemsPerPage],
  );

  const safePage = pageCount > 0 ? Math.min(currentPage, pageCount) : 1;

  const paginatedData = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, safePage, itemsPerPage]);

  const handleTypeTabChange = (newType) => {
    setSearchParams({ type: newType, page: '1' });
  };

  const handlePageChange = (event, value) => {
    setSearchParams({ type: typeTab, page: value.toString() });
  };

  const handleBookmarkToggle = async (item) => {
    const key = getItemKey(item);
    if (!key || readOnly || pendingBookmarks.current.has(key)) return;
    pendingBookmarks.current.add(key);
    setBookmarkError('');

    try {
      await deleteBookmark(item);
      setAllData((prev) => prev.filter((d) => getItemKey(d) !== key));
      queryClient.invalidateQueries({ queryKey: ['albumDetail'] });
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
    } catch (err) {
      if (item.category === 'Feed')
        setBookmarkError('저장을 해제하지 못했습니다. 다시 시도해 주세요.');
      console.error('Failed to toggle bookmark:', err);
    } finally {
      pendingBookmarks.current.delete(key);
    }
  };

  const renderActionButton = (item) => {
    if (readOnly) return null;
    return (
      <Box
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <BookmarkButton
          isBookmarked
          onToggle={() => handleBookmarkToggle(item)}
          sx={{
            color: '#777 !important',
            backgroundColor: theme.palette.common.white,
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.14)',
            '&:hover': {
              backgroundColor: theme.palette.common.white,
            },
          }}
        />
      </Box>
    );
  };

  const toReviewCardData = (item) => {
    const album = item.album || {};
    const review = item.review || {};
    const author =
      item.author || review.author || review.user || item.reviewAuthor || item.reviewer || {};
    const artist = item.artist ||
      album.artist || {
        name: album.artistName || '',
        sourceId: album.artistSourceId || album.artistId,
        artistSource: album.artistSource,
        slug: album.artistSlug,
        id: album.artistDbId,
      };
    const albumGenres =
      album.genres ||
      album.genreNames ||
      album.albumGenres ||
      item.genreNames ||
      item.albumGenres ||
      [];

    return {
      reviewId: review.id,
      content: review.content,
      rating: review.rating,
      createdAt: review.createdAt,
      likeCount: review.likeCount,
      commentCount: review.commentCount,
      albumId: album.id,
      albumSlug: album.slug,
      albumSource: album.albumSource,
      albumImageUrl: album.imageUrl,
      albumTitle: album.title,
      albumContentRating: album.contentRating,
      isStarred: album.isStarred,
      albumAvgRating: album.averageRating ?? album.avgRating ?? album.albumAvgRating,
      albumReviewCount: album.reviewCount ?? album.albumReviewCount,
      albumReleaseDate: album.releaseDate,
      albumGenres,
      artist,
      userNickname: author.nickname || review.userNickname || review.authorNickname,
      userProfileImageUrl:
        author.profileImageUrl || review.userProfileImageUrl || review.authorProfileImageUrl,
      userGrade: author.grade || review.userGrade || review.authorGrade,
    };
  };

  const renderSavedReviewCard = (item) => (
    <Box sx={{ minWidth: 0, position: 'relative' }}>
      {isXs && renderActionButton(item) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 3,
          }}
        >
          {renderActionButton(item)}
        </Box>
      )}
      <ReviewCard
        review={toReviewCardData(item)}
        variant="horizontalMobile"
        referrerType={REFERRER_TYPE.MYPAGE}
        coverActionButton={isXs ? null : renderActionButton(item)}
      />
    </Box>
  );

  const renderSavedAlbumHorizontalCard = (item) => (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      {!readOnly && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 2,
          }}
        >
          {renderActionButton(item)}
        </Box>
      )}
      <AlbumHorizontalCard
        sourceId={item.album.sourceId}
        albumSource={item.album.albumSource}
        slug={item.album.slug}
        imageUrl={item.album.imageUrl}
        albumName={item.album.title}
        contentRating={item.album.contentRating}
        isStarred={item.album.isStarred}
        artistName={item.artist?.name}
        artistId={item.artist?.sourceId}
        artistDbId={item.artist?.id}
        artistSource={item.artist?.artistSource}
        artistSlug={item.artist?.slug}
        albumAvgRating={item.album.averageRating}
        reviewCount={item.album.reviewCount}
        bookmarkCount={item.album.bookmarkCount}
        releaseDate={item.album.releaseDate}
        imageWidth={{ xs: '34%', md: '30%' }}
        imageMinWidth={{ xs: 112, md: 168 }}
        imageMaxWidth={{ xs: 136, md: 180 }}
        cardAlignItems="center"
        actionButton={null}
        referrerType={REFERRER_TYPE.MYPAGE}
      />
    </Box>
  );

  const toPlaylistCardData = (item) => ({
    id: item.list.id,
    title: item.list.title,
    imageUrl: item.list.imageUrl,
    type: item.list.type,
    albumCount: item.list.albumCount,
    trackCount: item.list.trackCount,
    commentCount: item.list.commentCount,
    bookmarkCount: item.list.bookmarkCount,
    createdAt: item.list.date,
    userId: item.author.id,
    username: item.author.nickname,
    userProfileImageUrl: item.author.profileImageUrl,
    userGrade: item.author.grade,
    slug: item.list.slug,
  });

  const renderSavedPlaylistCard = (item, { hideUser = false } = {}) => (
    <PlaylistCard
      playlist={toPlaylistCardData(item)}
      dateOnNewLine
      hideUser={hideUser}
      referrerType={REFERRER_TYPE.MYPAGE}
      thumbnailVariant="square"
      compactMeta={isXs}
      floatingActionButton={isXs}
      floatingActionButtonPlacement={isXs ? 'top' : 'center'}
      actionButton={renderActionButton(item)}
    />
  );

  const renderContent = () => {
    if (paginatedData.length === 0 && !loading) return <NoData />;

    if (isXs) {
      // 모바일: 가로형 카드 + Stack
      return (
        <Stack spacing={'15px'}>
          {paginatedData.map((item, index) => (
            <React.Fragment key={getItemKey(item)}>
              <Box>
                {renderHorizontalCard(item)}
                {item.category !== 'Reviews' && (
                  <Divider sx={{ borderColor: theme.palette.grey.line, mt: '15px' }} />
                )}
              </Box>
              {(index + 1) % itemsPerAd === 0 && index < paginatedData.length - 1 && (
                <InFeedAd pageType={PAGE_TYPE.MYPAGE} />
              )}
            </React.Fragment>
          ))}
        </Stack>
      );
    }

    // 태블릿/데스크탑: 세로형 카드 + Grid 4열
    return (
      <Box sx={{ overflow: 'hidden' }}>
        <Grid container columnSpacing={{ md: '16px', lg: '20px' }} rowSpacing={'40px'}>
          {paginatedData.map((item, index) => (
            <React.Fragment key={getItemKey(item)}>
              <Grid item xs={12} md={3}>
                {renderVerticalCard(item)}
              </Grid>
              {(index + 1) % itemsPerAd === 0 && index < paginatedData.length - 1 && (
                <Grid item xs={12}>
                  <InFeedAd pageType={PAGE_TYPE.MYPAGE} />
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderHorizontalCard = (item) => {
    const actionButton = renderActionButton(item);
    switch (item.category) {
      case 'Feed':
        return <FeedCard post={item.feed} actionButton={actionButton} />;
      case 'Reviews':
        return renderSavedReviewCard(item);
      case 'Albums':
        return renderSavedAlbumHorizontalCard(item);
      case 'News':
        return (
          <Box sx={{ position: 'relative', minWidth: 0, pr: actionButton ? 6.5 : 0 }}>
            {actionButton && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 3,
                }}
              >
                {actionButton}
              </Box>
            )}
            <NewsCard
              news={{
                id: item.news.id,
                title: item.news.title,
                imageUrl: item.news.imageUrl,
                sourceUrl: item.news.sourceUrl,
                linkUrl: item.news.linkUrl,
                releaseDate: item.news.date,
                commentCount: item.news.commentCount,
                bookmarkCount: item.news.bookmarkCount,
                slug: item.news.slug,
              }}
              actionButton={null}
              referrerType={REFERRER_TYPE.MYPAGE}
            />
          </Box>
        );
      case 'Lists':
        return renderSavedPlaylistCard(item);
      case 'Notice':
        return (
          <Box sx={{ position: 'relative', minWidth: 0, pr: actionButton ? 6.5 : 0 }}>
            {actionButton && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 3,
                }}
              >
                {actionButton}
              </Box>
            )}
            <NoticeCard
              notice={{
                id: item.notice.id,
                title: item.notice.title,
                imageUrl: item.notice.imageUrl,
                commentCount: item.notice.commentCount,
                bookmarkCount: item.notice.bookmarkCount,
                createdAt: item.notice.date,
                slug: item.notice.slug,
              }}
              actionButton={null}
              referrerType={REFERRER_TYPE.MYPAGE}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const renderVerticalCard = (item) => {
    const actionButton = renderActionButton(item);
    switch (item.category) {
      case 'Feed':
        return <FeedCard post={item.feed} actionButton={actionButton} />;
      case 'Reviews':
        return renderSavedReviewCard(item);
      case 'Albums':
        return (
          <AlbumVerticalCard
            sourceId={item.album.sourceId}
            albumSource={item.album.albumSource}
            slug={item.album.slug}
            imageUrl={item.album.imageUrl}
            albumName={item.album.title}
            contentRating={item.album.contentRating}
            isStarred={item.album.isStarred}
            artistName={item.artist?.name}
            artistId={item.artist?.sourceId}
            artistDbId={item.artist?.id}
            artistSource={item.artist?.artistSource}
            artistSlug={item.artist?.slug}
            albumAvgRating={item.album.averageRating}
            commentCount={item.album.reviewCount}
            bookmarkCount={item.album.bookmarkCount}
            releaseDate={item.album.releaseDate}
            ratingSolo
            dateSolo
            hideBookmarkCount
            actionButton={null}
            coverActionButton={actionButton}
            referrerType={REFERRER_TYPE.MYPAGE}
          />
        );
      case 'News':
        return (
          <NewsVerticalCard
            news={{
              id: item.news.id,
              title: item.news.title,
              imageUrl: item.news.imageUrl,
              sourceUrl: item.news.sourceUrl,
              linkUrl: item.news.linkUrl,
              releaseDate: item.news.date,
              commentCount: item.news.commentCount,
              bookmarkCount: item.news.bookmarkCount,
              slug: item.news.slug,
            }}
            statsOnNewLine={isMd}
            actionButton={null}
            coverActionButton={actionButton}
            referrerType={REFERRER_TYPE.MYPAGE}
          />
        );
      case 'Lists':
        return (
          <PlaylistVerticalCard
            playlist={toPlaylistCardData(item)}
            thumbnailVariant="square"
            actionButton={null}
            coverActionButton={actionButton}
            referrerType={REFERRER_TYPE.MYPAGE}
          />
        );
      case 'Notice':
        return (
          <NoticeVerticalCard
            notice={{
              id: item.notice.id,
              title: item.notice.title,
              imageUrl: item.notice.imageUrl,
              commentCount: item.notice.commentCount,
              bookmarkCount: item.notice.bookmarkCount,
              createdAt: item.notice.date,
              slug: item.notice.slug,
            }}
            actionButton={null}
            coverActionButton={actionButton}
            inlineStats
            referrerType={REFERRER_TYPE.MYPAGE}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ProgressBar isLoading={loading}>
      {bookmarkError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBookmarkError('')}>
          {bookmarkError}
        </Alert>
      )}
      {/* 서브탭 */}
      <Box sx={{ mb: { xs: '16px', md: '20px', lg: '30px' } }}>
        <SubTab tabs={subTabsWithCount} activeTab={typeTab} onTabChange={handleTypeTabChange} />
      </Box>
      {renderContent()}

      {pageCount > 1 && (
        <Pagination count={pageCount} page={safePage} onChange={handlePageChange} />
      )}
    </ProgressBar>
  );
};

// 아이템별 고유키 생성
function getItemKey(item) {
  switch (item.category) {
    case 'Feed':
      return item.feed?.id != null ? `feed-${item.feed.id}` : null;
    case 'Reviews':
      return item.review?.id != null ? `review-${item.review.id}` : null;
    case 'Albums':
      return item.album?.id != null ? `album-${item.album.id}` : null;
    case 'News':
      return item.news?.id != null ? `news-${item.news.id}` : null;
    case 'Lists':
      return item.list?.id != null ? `list-${item.list.id}` : null;
    case 'Notice':
      return item.notice?.id != null ? `notice-${item.notice.id}` : null;
    default:
      return null;
  }
}

// 북마크 삭제 API 호출
async function deleteBookmark(item) {
  switch (item.category) {
    case 'Feed':
      return feedsApi.bookmark(item.feed.id, true);
    case 'Reviews':
      return reviewsApi.deleteReviewBookmark(item.review?.id);
    case 'Albums':
      return reviewsApi.deleteBookmark(item.album?.id);
    case 'News':
      return newsApi.deleteBookmark(item.news?.id);
    case 'Lists':
      return playlistApi.deleteBookmark(item.list?.id);
    case 'Notice':
      return noticesApi.deleteBookmark(item.notice?.id);
    default:
      break;
  }
}

export default SavedTab;
